import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Trash2, Loader2 } from 'lucide-react';
import { removeFileFromOneTimeContract, downloadFile } from '@/service/weexpertService';
import { getDecodedFileName } from '@/lib/utils';
import { format } from 'date-fns';

const ExistingFilesList = ({ files, contractId, onFileRemoved }) => {
  const { t } = useTranslation(['common', 'contracts']);
  const [removingFileId, setRemovingFileId] = useState(null);

  const removeFileMutation = useMutation({
    mutationFn: async (fileId) => {
      return await removeFileFromOneTimeContract(contractId, fileId);
    },
    onSuccess: (_, fileId) => {
      toast({
        title: t('contracts:file_removed_successfully'),
        description: t('contracts:file_removed_description'),
        variant: "default",
      });
      setRemovingFileId(null);
      onFileRemoved();
    },
    onError: (error, fileId) => {
      console.error('Error removing file:', error);
      toast({
        title: t('common:error'),
        description: error.response?.data?.message || t('contracts:file_remove_error'),
        variant: "destructive",
      });
      setRemovingFileId(null);
    }
  });

  const handleRemoveFile = (fileId, fileName) => {
    if (window.confirm(t('contracts:confirm_remove_file', { fileName }))) {
      setRemovingFileId(fileId);
      removeFileMutation.mutate(fileId);
    }
  };

  const handleDownloadFile = (fileId) => {
    downloadFile(fileId);
  };

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <Card className="p-6">
      <h3 className="text-xl font-medium mb-4">{t('contracts:work_files_count', { count: files.length })}</h3>
      <div className="space-y-2">
        {files.map((file) => {
          const fileName = file.path ? getDecodedFileName(file.path) : (file.fileName || file.name || `File ${file.id}`);
          
          return (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-1">
                <span className="text-sm font-medium">{fileName}</span>
                {file.createdAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('contracts:added_on')}: {format(new Date(file.createdAt), 'dd.MM.yyyy HH:mm')}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadFile(file.id)}
                >
                  {t('contracts:download')}
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRemoveFile(file.id, fileName)}
                  disabled={removingFileId === file.id}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {removingFileId === file.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('contracts:remove')
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default ExistingFilesList;